import { useMemo, useState } from 'react'
import Sparkline from './Sparkline'
import { resolveColumnRef, resolveValue } from '../utils/chartDataUtils'

const formatNumber = (num) => {
  if (num === 0) return '0'
  if (num < 1000) return num.toString()
  if (num < 1000000) {
    const k = num / 1000
    const fixedPoint = num < 10000 ? 1 : 0
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(fixedPoint)}K`
  }
  const m = num / 1000000
  const fixedPoint = num < 10000000 ? 1 : 0
  return m % 1 === 0 ? `${m}M` : `${m.toFixed(fixedPoint)}M`
}

const resolveLabel = (value, dimensionId, dimensionInfo, columns) => {
  const column = resolveColumnRef(dimensionId, columns)
  if (column && column.source && column.source !== dimensionId) {
    // Use a synthetic row to resolve via the column's source (e.g. 'clientId.name')
    const row = { [dimensionId]: value }
    const resolved = resolveValue(row, column, dimensionInfo)
    if (resolved !== undefined && resolved !== null) return String(resolved)
  }
  return String(value)
}

export default function PivotTable({ data, chartOptions = {}, dimensionInfo, query }) {
  const {
    columns = [],
    rowDimension = 'clientId',
    columnDimension = 'feature',
    timeDimension = 'time',
    metric = 'count',
    rowLabel
  } = chartOptions

  const [sortColumn, setSortColumn] = useState(null)

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return null

    const hasComparison = data.some(d => d.comparisonPeriod !== undefined && d.comparisonPeriod !== null)
    const hasTime = data.some(d => d[timeDimension] !== undefined && d[timeDimension] !== null)

    // Split current vs comparison period data
    const currentData = hasComparison ? data.filter(d => d.comparisonPeriod === 0) : data
    const previousData = hasComparison ? data.filter(d => d.comparisonPeriod === -1) : []

    // Extract unique row, column, and time values
    const hasColumnDimension = columnDimension && currentData.some(d => d[columnDimension] !== undefined)
    const rowValues = [...new Set(currentData.map(d => d[rowDimension]))]
    const colValues = hasColumnDimension
      ? [...new Set(currentData.map(d => d[columnDimension]))]
      : ['_total']
    const timeValues = hasTime
      ? [...new Set(currentData.map(d => d[timeDimension]))].sort()
      : []

    // Build lookup: (row, col) -> array of time-ordered metric values
    const buildCellMap = (rows) => {
      const map = {}
      rows.forEach(d => {
        const colKey = hasColumnDimension ? d[columnDimension] : '_total'
        const key = `${d[rowDimension]}__${colKey}`
        if (!map[key]) map[key] = {}
        if (hasTime) {
          map[key][d[timeDimension]] = (map[key][d[timeDimension]] || 0) + (d[metric] || 0)
        } else {
          map[key]._total = (map[key]._total || 0) + (d[metric] || 0)
        }
      })
      return map
    }

    const currentMap = buildCellMap(currentData)
    const previousMap = hasComparison ? buildCellMap(previousData) : {}

    const buildCell = (rv, cv) => {
      const key = `${rv}__${cv}`
      const currentEntry = currentMap[key] || {}
      const previousEntry = previousMap[key] || {}

      let total, sparklineData, prevTotal, prevSparklineData

      if (hasTime) {
        sparklineData = timeValues.map(t => currentEntry[t] || 0)
        total = sparklineData.reduce((a, b) => a + b, 0)
      } else {
        total = currentEntry._total || 0
        sparklineData = []
      }

      if (hasComparison) {
        if (hasTime) {
          // Previous period may have different time buckets, use same count of values
          const prevTimeValues = [...new Set(previousData.map(d => d[timeDimension]))].sort()
          prevSparklineData = prevTimeValues.map(t => previousEntry[t] || 0)
          prevTotal = prevSparklineData.reduce((a, b) => a + b, 0)
        } else {
          prevTotal = previousEntry._total || 0
          prevSparklineData = []
        }
      }

      const delta = hasComparison ? total - (prevTotal || 0) : null
      const deltaPercent = hasComparison && prevTotal ? ((delta / prevTotal) * 100) : null

      return { total, sparklineData, prevTotal, prevSparklineData, delta, deltaPercent }
    }

    // Build grid
    const grid = {}
    rowValues.forEach(rv => {
      grid[rv] = {}
      colValues.forEach(cv => {
        grid[rv][cv] = buildCell(rv, cv)
      })
    })

    // Compute column maxes for heatmap intensity
    const colMaxes = {}
    colValues.forEach(cv => {
      colMaxes[cv] = Math.max(...rowValues.map(rv => grid[rv][cv].total), 1)
    })

    // Row totals for default sorting
    const rowTotals = {}
    rowValues.forEach(rv => {
      rowTotals[rv] = colValues.reduce((sum, cv) => sum + grid[rv][cv].total, 0)
    })

    return { rowValues, colValues, timeValues, grid, colMaxes, rowTotals, hasColumnDimension, hasComparison, hasTime }
  }, [data, rowDimension, columnDimension, timeDimension, metric])

  if (!processedData) {
    return <div className="empty-cell">No data available</div>
  }

  const { rowValues, colValues, grid, colMaxes, rowTotals, hasComparison, hasTime } = processedData

  // Sort rows
  const sortedRows = [...rowValues].sort((a, b) => {
    if (sortColumn) {
      return (grid[b][sortColumn]?.total || 0) - (grid[a][sortColumn]?.total || 0)
    }
    return (rowTotals[b] || 0) - (rowTotals[a] || 0)
  })

  const getRowLabel = (rv) => {
    if (rowLabel) {
      const row = data.find(d => d[rowDimension] === rv)
      if (row) {
        const label = rowLabel(row, dimensionInfo)
        if (label) return label
      }
    }
    return resolveLabel(rv, rowDimension, dimensionInfo, columns)
  }

  const handleHeaderClick = (cv) => {
    setSortColumn(prev => prev === cv ? null : cv)
  }

  return (
    <div className="pivot-table">
      <table className="usage-table">
        <thead>
          <tr>
            <th>{resolveColumnRef(rowDimension, columns).label || rowDimension}</th>
            {colValues.map(cv => (
              <th
                key={cv}
                className="center pivot-table__col-header"
                onClick={() => handleHeaderClick(cv)}
                title="Click to sort by this column"
              >
                {processedData.hasColumnDimension ? resolveLabel(cv, columnDimension, dimensionInfo, columns) : 'Total'}
                {sortColumn === cv && ' \u25BC'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(rv => (
            <tr key={rv}>
              <td className="client-name">{getRowLabel(rv)}</td>
              {colValues.map(cv => {
                const cell = grid[rv][cv]
                const hasData = cell.total > 0
                const barWidth = (cell.total / colMaxes[cv]) * 100
                const relativeSize = cell.total / colMaxes[cv]

                return (
                  <td key={cv}>
                    {hasData ? (
                      <div className="data-cell">
                        <div className="size-bar" style={{ width: `${barWidth}%` }} />
                        <span
                          className="data-value"
                          style={{
                            fontWeight: relativeSize > 0.7 ? 700 : relativeSize > 0.3 ? 600 : 500
                          }}
                        >
                          {formatNumber(cell.total)}
                        </span>
                        <div className="data-cell__right">
                          {hasTime && cell.sparklineData.length > 1 && (
                            <div className="sparkline-wrapper">
                              <Sparkline
                                data={cell.sparklineData}
                                comparisonData={hasComparison ? cell.prevSparklineData : undefined}
                                width={60}
                                height={25}
                                color="#3b82f6"
                                strokeWidth={1}
                              />
                            </div>
                          )}
                          {hasComparison && cell.deltaPercent !== null && (
                            <span className={`pivot-table__delta ${cell.delta >= 0 ? 'pivot-table__delta--up' : 'pivot-table__delta--down'}`}>
                              {cell.delta >= 0 ? '\u2191' : '\u2193'}
                              {Math.abs(cell.deltaPercent).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="empty-cell">-</div>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            {colValues.map(cv => {
              const colTotal = sortedRows.reduce((sum, rv) => sum + grid[rv][cv].total, 0)
              const colPrevTotal = hasComparison
                ? sortedRows.reduce((sum, rv) => sum + (grid[rv][cv].prevTotal || 0), 0)
                : null
              const colDelta = colPrevTotal !== null ? colTotal - colPrevTotal : null
              const colDeltaPercent = colPrevTotal ? (colDelta / colPrevTotal) * 100 : null
              const colSparkline = hasTime && processedData.timeValues.length > 1
                ? processedData.timeValues.map((_, idx) =>
                    sortedRows.reduce((sum, rv) => sum + (grid[rv][cv].sparklineData[idx] || 0), 0)
                  )
                : []

              return (
                <td key={cv}>
                  <div className="data-cell">
                    <span className="data-value">{formatNumber(colTotal)}</span>
                    <div className="data-cell__right">
                      {colSparkline.length > 1 && (
                        <div className="sparkline-wrapper">
                          <Sparkline
                            data={colSparkline}
                            width={60}
                            height={25}
                            color="#3b82f6"
                            strokeWidth={1.5}
                            fill
                          />
                        </div>
                      )}
                      {hasComparison && colDeltaPercent !== null && (
                        <span className={`pivot-table__delta ${colDelta >= 0 ? 'pivot-table__delta--up' : 'pivot-table__delta--down'}`}>
                          {colDelta >= 0 ? '\u2191' : '\u2193'}
                          {Math.abs(colDeltaPercent).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                </td>
              )
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
